/**
 * Menu의 presentational 레이어 — styled-components + Text variant 정의.
 *
 * 로직(`Menu.tsx`)과 결합이 없어 분리(레포의 `sliderStyles.ts` 패턴). 색/면은 모두 semantic
 * 토큰(`theme.color.*`)으로, 항목 색은 톤(default/warning/danger)·disabled에 따라 결정한다.
 */
import styled from "styled-components";

import { controlFocusRing, controlRadius, controlTransition } from "../foundations/controlStyles";
import { yScrollbarStyles } from "../foundations/scrollbar";
import type { TextVariantDefinition } from "../Text";
import type { MenuItemTone } from "./Menu";

export const MenuList = styled.div`
  display: grid;
  min-width: 9rem;
  min-height: 0;
  padding: 0.25rem;
  gap: 1px;
  /* AnchoredPopup 루트가 max-height 캡 + overflow:hidden이라, 리스트가 자체 스크롤을
     소유해야 캡을 넘는 긴 메뉴(marker cluster 등)의 항목이 잘리지 않는다. */
  ${yScrollbarStyles}
`;

export const ItemButton = styled.button<{ $tone: MenuItemTone; $disabled: boolean }>`
  display: flex;
  width: 100%;
  min-width: 0;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.6rem;
  border: 0;
  ${controlRadius}
  background: transparent;
  color: ${({ $tone, $disabled, theme }) => {
    if ($disabled) return theme.color.text.disabled;
    if ($tone === "danger") return theme.color.text.danger;
    if ($tone === "warning") return theme.color.text.warning;
    return theme.color.text.default;
  }};
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  font: inherit;
  text-align: left;
  ${controlTransition("background-color")}

  /* 강조 면은 roving focus 항목에만 둔다. hover는 Menu.tsx에서 focus로 이어지므로(focusOnHover)
     마우스든 키보드든 항상 한 항목만 강조한다. 마우스 focus는 :focus(면)만, 키보드는
     :focus-visible(ring)을 더한다. */
  &:focus {
    outline: none;
    background-color: ${({ theme }) => theme.color.bg.surfaceRaisedHigh};
  }

  &:focus-visible {
    ${controlFocusRing("-2px")}
  }
`;

export const ItemIcon = styled.span`
  display: inline-flex;
  width: 1em;
  height: 1em;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  color: inherit;
  font-size: ${({ theme }) => theme.typography.size.title};
`;

export const ItemTextColumn = styled.span`
  display: grid;
  min-width: 0;
  flex: 1 1 auto;
  gap: 0.05rem;
`;

export const SeparatorLine = styled.div`
  height: 1px;
  margin: 0.25rem 0.35rem;
  background-color: ${({ theme }) => theme.color.border.subtle};
`;

export const SectionGroup = styled.div`
  display: grid;
  gap: 1px;
`;

export const SectionHeading = styled.div`
  padding: 0.3rem 0.6rem 0.15rem;
`;

const regularWeight = "var(--c6-typography-weight-regular)";
const semiboldWeight = "var(--c6-typography-weight-semibold)";

export const itemLabelVariant = {
  as: "span",
  style: {
    display: "block",
    overflow: "hidden",
    color: "inherit",
    fontSize: "0.875rem",
    fontWeight: regularWeight,
    lineHeight: 1.4,
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
} satisfies TextVariantDefinition;

export const itemCaptionVariant = {
  as: "span",
  style: {
    display: "block",
    overflow: "hidden",
    color: "var(--c6-color-text-muted)",
    fontSize: "0.75rem",
    fontWeight: regularWeight,
    lineHeight: 1.35,
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
} satisfies TextVariantDefinition;

export const sectionHeadingVariant = {
  as: "span",
  style: {
    display: "block",
    color: "var(--c6-color-text-muted)",
    fontSize: "0.7rem",
    fontWeight: semiboldWeight,
    letterSpacing: "0.02em",
    lineHeight: 1.3,
    textTransform: "uppercase",
  },
} satisfies TextVariantDefinition;
